'use client';

import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as WebLink, Text, type WebTextProps } from '@galapagos/design-system/web';
import { PageTitle } from '../components/PageTitle';
import type { LegalBlock, LegalDocument, LegalRun, LegalTableCell } from './legal-document-schema';

type TextVariant = NonNullable<WebTextProps['variant']>;

/** A text size and the design-system Link variant drawn at the same size. */
interface TextStyle {
  text: TextVariant;
  link: 'inline' | 'small';
}

// Long-form reading: 16px on 24px lines, with links at the same 16px (`inline`).
const BODY: TextStyle = { text: 'b2Regular24', link: 'inline' };
// Table cells stay at 14px so four columns fit; `small` links match that size.
const CELL: TextStyle = { text: 'c1Regular', link: 'small' };
const HEADER_CELL: TextStyle = { text: 'c1Medium', link: 'small' };
const HEADING_VARIANT: Record<2 | 3 | 4, TextVariant> = { 2: 'h2Bold', 3: 'b1Bold', 4: 'b2Bold' };
const HEADING_TAG = { 2: 'h2', 3: 'h3', 4: 'h4' } as const;

/** Links to other sites open in a new tab; email, phone and in-app links open in place. */
function opensNewTab(href: string): boolean {
  return /^https?:/i.test(href);
}

function Runs({ runs, textStyle }: { runs: readonly LegalRun[]; textStyle: TextStyle }) {
  const { t } = useTranslation('signup');
  return (
    <>
      {runs.map((run, index) => {
        if ('lineBreak' in run) return <br key={index} />;
        if ('href' in run) {
          const newTab = opensNewTab(run.href);
          return (
            <WebLink
              key={index}
              label={run.text}
              href={run.href}
              variant={textStyle.link}
              opensNewTab={newTab}
              newTabLabel={newTab ? t('footer.opensInNewTab') : undefined}
              actionId="signup.legal-document-link.activate"
              testID="signup-legal-document-link"
            />
          );
        }
        // Runs are fragments of one paragraph, so the orphan binding (which
        // joins a block's last two words) would land mid-sentence.
        return (
          <Text
            key={index}
            value={run.text}
            variant={textStyle.text}
            preventOrphanBinding={false}
          />
        );
      })}
    </>
  );
}

function List({
  items,
  textStyle,
}: {
  items: readonly (readonly LegalRun[])[];
  textStyle: TextStyle;
}) {
  return (
    <ul className="signup-legal__list">
      {items.map((runs, index) => (
        <li key={index}>
          <Runs runs={runs} textStyle={textStyle} />
        </li>
      ))}
    </ul>
  );
}

function Cell({ cell, textStyle }: { cell: LegalTableCell; textStyle: TextStyle }) {
  return (
    <>
      {cell.map((block, index) =>
        block.type === 'list' ? (
          <List key={index} items={block.items} textStyle={textStyle} />
        ) : (
          <p key={index} className="signup-legal__paragraph">
            <Runs runs={block.runs} textStyle={textStyle} />
          </p>
        ),
      )}
    </>
  );
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.type) {
    case 'heading': {
      const Heading = HEADING_TAG[block.level];
      return (
        <Heading id={block.anchorId} className="signup-legal__heading">
          <Text value={block.text} variant={HEADING_VARIANT[block.level]} />
        </Heading>
      );
    }
    case 'paragraph':
      return (
        <p className="signup-legal__paragraph">
          <Runs runs={block.runs} textStyle={BODY} />
        </p>
      );
    case 'list':
      return <List items={block.items} textStyle={BODY} />;
    case 'table':
      return (
        <table className="signup-legal__table">
          {block.header ? (
            <thead>
              <tr>
                {block.header.map((cell, index) => (
                  <th key={index} scope="col" className="signup-legal__cell">
                    <Cell cell={cell} textStyle={HEADER_CELL} />
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, index) => (
                  <td key={index} className="signup-legal__cell">
                    <Cell cell={cell} textStyle={CELL} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
  }
}

/** One legal document: its title as the page's single `h1`, then its blocks in order. */
export function LegalDocumentView({
  document,
  headingRef,
}: {
  document: LegalDocument;
  headingRef?: Ref<HTMLHeadingElement>;
}) {
  return (
    // The documents exist in English only, so they say so even when the page
    // language is Spanish, and screen readers pronounce them as English.
    <article className="signup-legal" data-testid="signup-legal-document" lang="en">
      <PageTitle title={document.title} headingRef={headingRef} />
      {document.blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </article>
  );
}
